import { useState, useEffect, useCallback } from 'react';
import { supabase, useAuth } from '@mondivago/supabase';
import { useSupplierProfile } from './useSupplierProfile';
import type { TourRow, EnrichedTourRow, TourWithRelations } from '@mondivago/types';

export function useSupplierTours(allAgencyTours = false) {
  const { agencyId } = useAuth();
  const { profile, agency, type } = useSupplierProfile();
  const supplierId = profile?.id ?? agency?.id ?? null;
  const isAgencyAdmin = type === 'agency' && !!agencyId;
  const shouldFetchAllSupplierCompany = (isAgencyAdmin || allAgencyTours) && !!agencyId;
  const [tours, setTours] = useState<EnrichedTourRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTours = useCallback(async () => {
    if (!supplierId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('tours')
        .select(
          '*, content_cities(name, slug, content_countries(name, slug)), traveler_reviews(rating, supplier_reply), tour_addons(*), tour_pricing_rules(*), tour_minimum_prices(min_price)'
        )
        .order('created_at', { ascending: false });

      if (shouldFetchAllSupplierCompany && agencyId) {
        const { data: agentIds } = await supabase
          .from('supplier_workers')
          .select('supplier_id')
          .eq('supplier_id', agencyId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const supplierIds = agentIds?.map((a: any) => a.supplier_id) || [];
        const allIds = [agencyId, ...supplierIds].filter(Boolean);
        query = query.in('supplier_id', allIds);
      } else {
        query = query.eq('supplier_id', supplierId);
      }

      const { data, error: dbError } = await query;
      if (dbError) throw new Error(dbError.message || 'Database error');

      setTours(
        ((data as unknown as TourWithRelations[]) || []).map((tour) => {
          const reviews = tour.traveler_reviews || [];
          const reviewsCount = reviews.length;
          return {
            ...tour,
            city: tour.content_cities?.name ?? '',
            citySlug: tour.content_cities?.slug ?? '',
            country: tour.content_cities?.content_countries?.name ?? '',
            countrySlug: tour.content_cities?.content_countries?.slug ?? '',
            average_rating:
              reviewsCount > 0
                ? Number((reviews.reduce((acc, r) => acc + (r.rating || 0), 0) / reviewsCount).toFixed(1))
                : undefined,
            reviews_count: reviewsCount,
            pending_replies_count: reviews.filter((r) => !r.supplier_reply).length,
          };
        })
      );
    } catch {
      setError('Error loading tours. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [supplierId, shouldFetchAllSupplierCompany, agencyId]);

  const createTour = useCallback(
    async (tour: Omit<TourRow, 'id'> & { supplier_id: string }) => {
      try {
        const { data, error: dbError } = await supabase.from('tours').insert(tour).select('id').single();
        if (dbError || !data) throw new Error(dbError?.message || 'Error creating tour.');
        void fetchTours();
        return { success: true, id: data.id };
      } catch {
        return { success: false, error: 'Error creating tour.' };
      }
    },
    [fetchTours]
  );

  const updateTour = useCallback(async (tourId: string, updates: Partial<TourRow>) => {
    try {
      const { error: dbError } = await supabase.from('tours').update(updates).eq('id', tourId);
      if (dbError) throw dbError;
      setTours((prev) => prev.map((t) => (t.id === tourId ? { ...t, ...updates } : t)));
      return { success: true };
    } catch {
      return { success: false, error: 'Error updating tour.' };
    }
  }, []);

  const deleteTour = useCallback(async (tourId: string) => {
    try {
      // tour_availabilities has ON DELETE RESTRICT, so we must delete its rows
      // first. bookings.availability_id is ON DELETE SET NULL so bookings are safe.
      const { error: availError } = await supabase.from('tour_availabilities').delete().eq('tour_id', tourId);
      if (availError) throw availError;

      const { error: dbError } = await supabase.from('tours').delete().eq('id', tourId);
      if (dbError) throw dbError;

      setTours((prev) => prev.filter((t) => t.id !== tourId));
      return { success: true };
    } catch {
      return { success: false, error: 'Error deleting tour.' };
    }
  }, []);

  const archiveTour = useCallback(async (tourId: string) => updateTour(tourId, { status: 'archived' }), [updateTour]);

  useEffect(() => {
    void fetchTours();
  }, [fetchTours]);

  return { tours, loading, error, refresh: fetchTours, createTour, updateTour, deleteTour, archiveTour };
}

export function useSupplierTour(tourId?: string) {
  const [tour, setTour] = useState<EnrichedTourRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTour = useCallback(async () => {
    if (!tourId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('tours')
        .select(
          '*, content_cities(name, slug, content_countries(name, slug)), tour_destinations(*), tour_pricing_rules(*), tour_minimum_prices(min_price)'
        )
        .eq('id', tourId)
        .single();
      if (dbError) throw dbError;
      const typed = data as unknown as TourWithRelations;
      if (typed && (!typed.tour_pricing_rules || typed.tour_pricing_rules.length === 0)) {
        const { data: pr } = await supabase.from('tour_pricing_rules').select('*').eq('tour_id', tourId);
        if (pr) typed.tour_pricing_rules = pr;
      }
      setTour(
        typed
          ? {
            ...typed,
            city: typed.content_cities?.name ?? '',
            citySlug: typed.content_cities?.slug ?? '',
            country: typed.content_cities?.content_countries?.name ?? '',
            countrySlug: typed.content_cities?.content_countries?.slug ?? '',
          }
          : null
      );
    } catch {
      setError('Error loading tour.');
    } finally {
      setLoading(false);
    }
  }, [tourId]);

  const updateTourFn = useCallback(
    async (updates: Partial<TourRow>) => {
      if (!tourId) return { success: false, error: 'Tour ID is missing.' };
      try {
        const { error: dbError } = await supabase.from('tours').update(updates).eq('id', tourId);
        if (dbError) throw dbError;
        setTour((prev) => (prev ? { ...prev, ...updates } : null));
        return { success: true };
      } catch {
        return { success: false, error: 'Error updating tour.' };
      }
    },
    [tourId]
  );

  useEffect(() => {
    void fetchTour();
  }, [fetchTour]);
  return { tour, loading, error, refresh: fetchTour, updateTour: updateTourFn };
}
